import { push } from 'react-router-redux';
import { call, put, takeLatest } from 'redux-saga/effects';
import message from 'antd/lib/message';
import { t } from '../../../app/i18n';
import {
  createApiActionCreators,
  createActionCreator,
  createReducer,
  createActionType,
  replaceInArray,
  REQUEST,
  SUCCESS,
  FAILURE,
} from '../../../common/utils/reduxHelpers';
import { showModal, hideModal } from '../../../features/modal/ducks';
import api, { apiCallIds } from '../api';
import { loanStatus } from '../../../common/enums';
import { uploadFile } from '../../files/ducks/index';
import { fetchPendingContractsActions, fetchUnsignedContractsActions } from '../../auth/ducks/index';

/**
 * ACTION TYPES
 */
export const FETCH_LOANS = 'loans/FETCH_LOANS';
export const FETCH_ANONYMIZED_LOANS = 'loans/FETCH_ANONYMIZED_LOANS';
export const SET_LOANS_PAGINATION = 'loans/SET_LOANS_PAGINATION';
export const SET_LOANS_FILTER = 'loans/SET_LOANS_FILTER';
export const CREATE_LOAN = 'loans/CREATE_LOAN';
export const SIGN_LOAN = 'loans/SIGN_LOAN';
export const SIGN_LOAN_INSURANCE = 'loans/SIGN_LOAN_INSURANCE';
export const CONFIRM_LOAN = 'loans/CONFIRM_LOAN';
export const CONFIRM_LOAN_INSURANCE = 'loans/CONFIRM_LOAN_INSURANCE';
export const SIGN_INFO_OBLIGATIONS = 'loans/SIGN_INFO_OBLIGATIONS';
export const CONFIRM_INFO_OBLIGATIONS = 'loans/CONFIRM_INFO_OBLIGATIONS';
export const SIGN_POST_FINANCING = 'loans/SIGN_POST_FINANCING';
export const CONFIRM_POST_FINANCING = 'loans/CONFIRM_POST_FINANCING';
export const RESEND_SMS_CODE_LOAN = 'loans/RESEND_SMS_CODE_LOAN';
export const UPDATE_LOANS_ENCASHMENT_STATUS = 'loans/UPDATE_LOANS_ENCASHMENT_STATUS';
export const CLEAR_LOANS_ENCASHMENT = 'loans/CLEAR_LOANS_ENCASHMENT';
export const CARD_PAYMENT = 'CARD_PAYMENT';
export const CLEAR_CARD_PAYMENT = 'CLEAR_CARD_PAYMENT';
export const DISABLE_LOAN_AUTOMATIC_PAYMENTS = 'loans/DISABLE_LOAN_AUTOMATIC_PAYMENTS';
export const DISABLE_SAVED_CARD = 'loans/DISABLE_SAVED_CARD';
export const ADD_LOAN_DOCUMENT = 'loans/ADD_LOAN_DOCUMENT';
export const REMOVE_LOAN_DOCUMENT = 'loans/REMOVE_LOAN_DOCUMENT';
export const WITHDRAW_LOAN = 'loans/WITHDRAW_LOAN';

/**
 * ACTIONS
 */
export const fetchLoansActions = createApiActionCreators(FETCH_LOANS);
export const fetchAnonymizedLoansActions = createApiActionCreators(FETCH_ANONYMIZED_LOANS);
export const createLoanActions = createApiActionCreators(CREATE_LOAN);
export const signLoanActions = createApiActionCreators(SIGN_LOAN);
export const confirmLoanActions = createApiActionCreators(CONFIRM_LOAN);
export const signLoanInsuranceActions = createApiActionCreators(SIGN_LOAN_INSURANCE);
export const confirmLoanInsuranceActions = createApiActionCreators(CONFIRM_LOAN_INSURANCE);
export const signInfoObligationsActions = createApiActionCreators(SIGN_INFO_OBLIGATIONS);
export const confirmInfoObligationsActions = createApiActionCreators(CONFIRM_INFO_OBLIGATIONS);
export const signPostFinancingActions = createApiActionCreators(SIGN_POST_FINANCING);
export const confirmPostFinancingActions = createApiActionCreators(CONFIRM_POST_FINANCING);
export const resendSmsCodeLoanRequest = createActionCreator(RESEND_SMS_CODE_LOAN);
export const setLoansPaginationAction = createActionCreator(SET_LOANS_PAGINATION);
export const setLoansFilterAction = createActionCreator(SET_LOANS_FILTER);
export const updateLoansEncashmentStatusActions = createApiActionCreators(UPDATE_LOANS_ENCASHMENT_STATUS);
export const clearLoansEncashmentStatusActions = createActionCreator(CLEAR_LOANS_ENCASHMENT);
export const cardPaymentActions = createApiActionCreators(CARD_PAYMENT);
export const clearCardPaymentActions = createActionCreator(CLEAR_CARD_PAYMENT);
export const disableLoanAutomaticPaymentsRequest = createActionCreator(DISABLE_LOAN_AUTOMATIC_PAYMENTS);
export const disableSavedCardRequest = createActionCreator(DISABLE_SAVED_CARD);
export const addLoanDocumentActions = createApiActionCreators(ADD_LOAN_DOCUMENT);
export const removeLoanDocumentActions = createApiActionCreators(REMOVE_LOAN_DOCUMENT);
export const withdrawLoanActions = createApiActionCreators(WITHDRAW_LOAN);

/**
 * REDUCERS
 */
const initialState = {
  items: [],
  encashmentStatusChangeResult: null,
  confirmError: null,
  pagination: {
    current: null,
    pageSize: null,
    total: null,
  },
  filter: {
    status: loanStatus.SCH,
    amountMin: null,
    amountMax: null,
    interestMin: null,
    interestMax: null,
    remainingAmountMin: null,
    remainingAmountMax: null,
    termMonthsMin: '',
    termMonthsMax: '',
  },
  cardPaymentURL: null,
};

const loans = createReducer(initialState, {
  [FETCH_LOANS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.append ? [...state.items, ...payload.items.data] : payload.items.data,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
  [FETCH_ANONYMIZED_LOANS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.append ? [...state.items, ...payload.items.data] : payload.items.data,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
  [CREATE_LOAN]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: [payload, ...state.items],
    }),
  },
  [SIGN_LOAN]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: replaceInArray(state.items, l => l.id === payload.id, payload),
    }),
  },
  [SIGN_LOAN_INSURANCE]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: replaceInArray(state.items, l => l.id === payload.id, payload),
    }),
  },
  [CONFIRM_LOAN]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: replaceInArray(state.items, l => l.id === payload.id, payload),
    }),
  },
  [CONFIRM_LOAN_INSURANCE]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: replaceInArray(state.items, l => l.id === payload.id, payload),
    }),
  },
  [SIGN_INFO_OBLIGATIONS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: replaceInArray(state.items, l => l.id === payload.id, payload),
    }),
  },
  [CONFIRM_INFO_OBLIGATIONS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      confirmError: null,
      items: replaceInArray(state.items, l => l.id === payload.id, payload),
    }),
    [FAILURE]: (state, payload) => ({
      ...state,
      confirmError: payload,
    }),
  },
  [SIGN_POST_FINANCING]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: replaceInArray(state.items, l => l.id === payload.id, payload),
    }),
  },
  [CONFIRM_POST_FINANCING]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      confirmError: null,
      items: replaceInArray(state.items, l => l.id === payload.id, payload),
    }),
    [FAILURE]: (state, payload) => ({
      ...state,
      confirmError: payload,
    }),
  },
  [SET_LOANS_PAGINATION]: (state, payload) => ({
    ...state,
    pagination: {
      current: payload.pageNumber + 1,
      pageSize: payload.pageSize,
      total: payload.total,
    },
  }),
  [SET_LOANS_FILTER]: (state, payload) => ({
    ...state,
    filter: payload.options,
  }),
  [UPDATE_LOANS_ENCASHMENT_STATUS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      encashmentStatusChangeResult: payload,
    }),
  },
  [CLEAR_LOANS_ENCASHMENT]: (state, payload) => ({
    ...state,
    encashmentStatusChangeResult: null,
  }),
  [CARD_PAYMENT]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      cardPaymentURL: payload
    })
  },
  [CLEAR_CARD_PAYMENT]: (state, payload) => ({
    ...state,
    cardPaymentURL: null,
  }),
  [WITHDRAW_LOAN]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: replaceInArray(state.items, l => l.id === payload.id, payload),
    }),
  },
});

export default loans;

/**
 * SELECTORS
 */
export const selectLoansState = state => state.loans;
export const selectLoans = state => selectLoansState(state).items;
export const selectLoansPagination = state => selectLoansState(state).pagination;
export const selectLoansFilter = state => selectLoansState(state).filter;
export const selectLoansEcashmentStatusChangeResult = state => selectLoansState(state).encashmentStatusChangeResult;
export const selectPaymentUrl = state => selectLoansState(state).cardPaymentURL;
export const selectConfirmError = state => selectLoansState(state).confirmError;

/**
 * SAGAS
 */
function* fetchLoans({ payload }) {
  const resp = yield call(api.fetchLoans, payload.options);

  if (resp.ok) {
    yield put(fetchLoansActions.success({ items: resp.data, append: payload.append }));
    yield put(setLoansPaginationAction(resp.data));
  } else {
    yield put(fetchLoansActions.failure(resp.error));
  }
}

function* fetchAnonymizedLoans({ payload }) {
  const resp = yield call(api.fetchAnonymizedLoans, payload.options);

  if (resp.ok) {
    yield put(fetchAnonymizedLoansActions.success({ items: resp.data, append: payload.append }));
    yield put(setLoansPaginationAction(resp.data));
  } else {
    yield put(fetchAnonymizedLoansActions.failure(resp.error));
  }
}

function* createLoan({ payload }) {
  const resp = yield call(api.createLoan, payload);

  if (resp.ok) {
    yield put(createLoanActions.success(resp.data));

    // fetch all pending contracts
    yield put(fetchUnsignedContractsActions.request({ accountId: resp.data.accountId }));
    yield put(fetchPendingContractsActions.request({ accountId: resp.data.accountId }));
    yield put(push('/dashboard/loans'));

    // yield put(
    //   showModal({
    //     id: apiCallIds.SIGN_LOAN,
    //     data: resp.data,
    //   })
    // );
    
  } else {
    yield put(createLoanActions.failure(resp.error));
  }
}

function* cardPayment({ payload }){

  const { loanId, data } = payload;
  const resp = yield call(api.cardPayment, loanId, data)

  if (resp.ok) {
    yield put(cardPaymentActions.success(resp.data))
  }
  else {
    yield put(cardPaymentActions.failure(resp.error))
  }
}

function* signLoan({ payload }) {
  const { loanId } = payload;

  const resp = yield call(api.signLoan, loanId);

  if (resp.ok) {
    yield put(signLoanActions.success(resp.data));

    yield put(hideModal(apiCallIds.SIGN_LOAN));

    yield put(
      showModal({
        id: apiCallIds.CONFIRM_LOAN,
        data: resp.data.id,
      })
    );
  } else {
    yield put(signLoanActions.failure(resp.error));
  }
}

function* signLoanInsurance({ payload }) {
  const { loanId } = payload;

  const resp = yield call(api.signLoanInsurance, loanId);

  if (resp.ok) {
    yield put(signLoanInsuranceActions.success(resp.data));

    yield put(hideModal(apiCallIds.SIGN_LOAN_INSURANCE));

    yield put(
      showModal({
        id: apiCallIds.CONFIRM_LOAN_INSURANCE,
        data: resp.data.id,
      })
    );
  } else {
    yield put(signLoanInsuranceActions.failure(resp.error));
  }
}

function* confirmLoan({ payload }) {
  const { loanId, token } = payload;

  const resp = yield call(api.confirmLoan, loanId, token);

  if (resp.ok) {
    yield put(confirmLoanActions.success(resp.data));
    yield put(
      fetchLoansActions.request({
        append: false,
        options: {},
      })
    );
    yield put(fetchPendingContractsActions.request({ accountId: resp.data.AccountId || resp.data.accountId }));
    yield put(hideModal(apiCallIds.CONFIRM_LOAN));
  } else {
    yield put(confirmLoanActions.failure(resp.error));
  }
}

function* confirmLoanInsurance({ payload }) {
  const { loanId, token } = payload;

  const resp = yield call(api.confirmLoanInsurance, loanId, token);

  if (resp.ok) {
    yield put(confirmLoanInsuranceActions.success(resp.data));
    yield put(
      fetchLoansActions.request({
        append: false,
        options: {},
      })
    );
    yield put(hideModal(apiCallIds.CONFIRM_LOAN_INSURANCE));
  } else {
    yield put(confirmLoanInsuranceActions.failure(resp.error));
  }
}

function* signInfoObligations({ payload }) {
  const { loanId, onSuccess, onError } = payload;

  const resp = yield call(api.signInfoObligations, loanId);

  if (resp.ok) {
    yield put(signInfoObligationsActions.success(resp.data));
    yield put(hideModal(apiCallIds.SIGN_INFO_OBLIGATIONS));
    yield put(
      showModal({
        id: apiCallIds.CONFIRM_INFO_OBLIGATIONS,
        data: resp.data.id,
      })
    );
    if (onSuccess) {
      onSuccess();
    }
  } else {
    yield put(signInfoObligationsActions.failure(resp.error));
    if (onError) {
      onError(resp.error);
    }
  }
}

function* confirmInfoObligations({ payload }) {
  const { loanId, token } = payload;

  const resp = yield call(api.confirmInfoObligations, loanId, token);

  if (resp.ok) {
    yield put(confirmInfoObligationsActions.success(resp.data));
    yield put(
      fetchLoansActions.request({
        append: false,
        options: {},
      })
    );
    yield put(fetchPendingContractsActions.request({ accountId: resp.data.AccountId || resp.data.accountId }));
    yield put(fetchUnsignedContractsActions.request({ accountId: resp.data.AccountId || resp.data.accountId }));
    yield put(hideModal(apiCallIds.CONFIRM_INFO_OBLIGATIONS));
  } else {
    yield put(confirmInfoObligationsActions.failure(resp.error));
  }
}

function* signPostFinancing({ payload }) {
  const { loanId, onSuccess, onError } = payload;

  const resp = yield call(api.signPostFinancing, loanId);

  if (resp.ok) {
    yield put(signPostFinancingActions.success(resp.data));
    yield put(hideModal(apiCallIds.SIGN_POST_FINANCING));
    yield put(
      showModal({
        id: apiCallIds.CONFIRM_POST_FINANCING,
        data: resp.data.id,
      })
    );
    if (onSuccess) {
      onSuccess();
    }
  } else {
    yield put(signPostFinancingActions.failure(resp.error));
    if (onError) {
      onError(resp.error);
    }
  }
}

function* confirmPostFinancing({ payload }) {
  const { loanId, token } = payload;

  const resp = yield call(api.confirmPostFinancing, loanId, token);

  if (resp.ok) {
    yield put(confirmPostFinancingActions.success(resp.data));
    yield put(
      fetchLoansActions.request({
        append: false,
        options: {},
      })
    );
    yield put(hideModal(apiCallIds.CONFIRM_POST_FINANCING));
  } else {
    yield put(confirmPostFinancingActions.failure(resp.error));
    message.error(resp.error || t('Podpis zmlúv sa nepodarilo dokončiť. Skúste znova.'), 5);
    // Modal intentionally stays open so the debtor can retry with a valid SMS token.
  }
}

function* resendSmsCodeLoan(action) {
  const { loanId, onSuccess, onError } = action.payload;
  const resp = yield call(api.resendSmsCodeLoan, loanId);

  if (resp.ok) {
    message.success(t('SMS kód bol preposlaný'), 3);
    yield put(fetchLoansActions.request({ append: false, options: {} }));
    if (onSuccess) {
      onSuccess();
    }
  } else {
    if (onError) {
      onError(resp.error);
    }
  }
}

function* updateLoansEncashmentStatus({ payload }) {
  const { loans, encashmentStatus } = payload;
  const resp = yield call(api.updateLoansEncashmentStatus, loans, encashmentStatus);

  if (resp.ok) {
    yield put(updateLoansEncashmentStatusActions.success(resp.data));
  } else {
    yield put(updateLoansEncashmentStatusActions.failure(resp.error));
  }
}

function* disableLoanAutomaticPayments(action) {
  const { loanId } = action.payload;
  const resp = yield call(api.disableAutomaticPayments, loanId);

  if (resp.ok) {
    message.success(t('Automatické splátky boli vypnuté'), 3);
  }
}

function* disableSavedCard(action) {
  const { loanId } = action.payload;
  const resp = yield call(api.disableSavedCard, loanId);

  if (resp.ok) {
    message.success(t('Karta bola vymazaná'), 3);
  }
}

function* addLoanDocument({ payload }) {
  const uploadFileResp = yield uploadFile({
    payload: payload.uploadFilePayload,
  });

  if (uploadFileResp.ok) {
    const resp = yield call(api.addLoanDocument, payload.loanId, {
      file: {
        id: uploadFileResp.data.id,
        name: uploadFileResp.data.name,
      },
      type: payload.type
    });

    if (resp.ok) {
      yield put(confirmLoanActions.success(resp.data));
    }
  }
}

function* removeLoanDocument({ payload }) {
  const resp = yield call(api.removeLoanDocument, payload.loanId);

  if (resp.ok) {
    yield put(confirmLoanActions.success(resp.data));
  }
}

function* withdrawLoan({ payload }) {
  const { loanId, onSuccess, onError } = payload;

  const resp = yield call(api.withdrawLoan, loanId);

  if (resp.ok) {
    yield put(withdrawLoanActions.success(resp.data));
    if (onSuccess) {
      onSuccess();
    }
  } else {
    yield put(withdrawLoanActions.failure(resp.error));
    const statusCode = resp.response && resp.response.status;
    if (onError) {
      onError(statusCode);
    }
  }
}

export function* loansSaga() {
  yield takeLatest(createActionType(FETCH_LOANS, REQUEST), fetchLoans);
  yield takeLatest(createActionType(FETCH_ANONYMIZED_LOANS, REQUEST), fetchAnonymizedLoans);
  yield takeLatest(SET_LOANS_FILTER, fetchAnonymizedLoans);
  yield takeLatest(createActionType(CREATE_LOAN, REQUEST), createLoan);
  yield takeLatest(createActionType(SIGN_LOAN, REQUEST), signLoan);
  yield takeLatest(createActionType(SIGN_LOAN_INSURANCE, REQUEST), signLoanInsurance);
  yield takeLatest(createActionType(CONFIRM_LOAN, REQUEST), confirmLoan);
  yield takeLatest(createActionType(CONFIRM_LOAN_INSURANCE, REQUEST), confirmLoanInsurance);
  yield takeLatest(createActionType(SIGN_INFO_OBLIGATIONS, REQUEST), signInfoObligations);
  yield takeLatest(createActionType(CONFIRM_INFO_OBLIGATIONS, REQUEST), confirmInfoObligations);
  yield takeLatest(createActionType(SIGN_POST_FINANCING, REQUEST), signPostFinancing);
  yield takeLatest(createActionType(CONFIRM_POST_FINANCING, REQUEST), confirmPostFinancing);
  yield takeLatest(RESEND_SMS_CODE_LOAN, resendSmsCodeLoan);
  yield takeLatest(createActionType(UPDATE_LOANS_ENCASHMENT_STATUS, REQUEST), updateLoansEncashmentStatus);
  yield takeLatest(createActionType(CARD_PAYMENT, REQUEST), cardPayment);
  yield takeLatest(DISABLE_LOAN_AUTOMATIC_PAYMENTS, disableLoanAutomaticPayments);
  yield takeLatest(DISABLE_SAVED_CARD, disableSavedCard);
  yield takeLatest(createActionType(ADD_LOAN_DOCUMENT, REQUEST), addLoanDocument);
  yield takeLatest(createActionType(REMOVE_LOAN_DOCUMENT, REQUEST), removeLoanDocument);
  yield takeLatest(createActionType(WITHDRAW_LOAN, REQUEST), withdrawLoan);
}



// WEBPACK FOOTER //
// ./src/features/loans/ducks/index.js